import { CloseIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { LocalizedString } from "../payment-zone-area.type";

type LocalizedTextProps = {
  value: LocalizedString[];
  onChange: (value: LocalizedString[]) => void;
  availableLanguages?: { code: string; label: string }[];
};

const DEFAULT_LANGUAGES = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
];

export const LocalizedText = ({ value, onChange, availableLanguages = DEFAULT_LANGUAGES }: LocalizedTextProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [text, setText] = useState<string>("");

  const handleAdd = () => {
    if (!selectedLanguage || !text.trim()) return;

    onChange([...value, { locale: selectedLanguage, text: text }]);

    setText("");
    setSelectedLanguage("");
  };

  const handleRemove = (locale: string) => {
    onChange(value.filter((item) => item.locale !== locale));
  };

  const getLanguageLabel = (code: string) => {
    return availableLanguages.find((lang) => lang.code === code)?.label || code;
  };

  const availableToAdd = availableLanguages.filter((lang) => !value.some((item) => item.locale === lang.code));

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
            <SelectTrigger>
              <SelectValue placeholder="Sélectionnez une langue" />
            </SelectTrigger>
            <SelectContent>
              {availableToAdd.map((lang) => (
                <SelectItem key={lang.code} value={lang.code}>
                  {lang.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-2">
          <Input
            placeholder="Entrez le texte"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAdd();
              }
            }}
          />
        </div>

        <Button type="button" variant="outline" onClick={handleAdd} disabled={!selectedLanguage || !text.trim()}>
          Ajouter
        </Button>
      </div>

      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((item) => (
            <div key={item.locale} className="flex items-center gap-2 bg-muted px-3 py-2 rounded-md">
              <span className="text-xs font-semibold text-muted-foreground min-w-20">
                {getLanguageLabel(item.locale)}
              </span>
              <span className="flex-1 text-sm">{item.text}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => handleRemove(item.locale)}
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
              >
                <CloseIcon className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
